import * as React from 'react'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Text } from '@amzn/dvui/dist/Text'
import { Modal } from '@amzn/dvui/dist/Modal'
import { PlayBox } from '../playButton/PlayButton'
import { StringId } from '../localizer/stringId'
import { TextLocalizer } from '../localizer/localizer'
import { isLiveEntityType } from '../../utils/entityTypes'
import { getPlaybackClickstreamRef } from '../../utils/getPlaybackClickstreamRef'
import { getPlaybackProgress } from '../../utils/getPlaybackProgress'
import {
  getFirstPlaybackData,
  getPlaybackSecondaryMessages,
} from '../../utils/playbackActionHelper'
import * as st from './episodePlayback.module.scss'
import * as cn from 'classnames'
import { DecodedText } from '../decodedText/decodedText'
import { withLocalization, LocalizerProps } from '../localizer/localizer'
import { TransactionOverlay } from '../actionBox/disableOverlay'
import { widgetNameClasses } from '../../utils/widgetNameClasses'
import { PurchaseOffer } from '../../selectors/purchase'
import { MaybeSpaContextProps, withMaybeSpaContext } from '@shared/SPA/SpaContext'
import { triggerPlayback as triggerWindowsPlayback } from '@shared/SPA/triggers'
import {
  LicenseModal
} from '@shared/SPA/downloadButton/LicenseModal'
import { PlayerContext } from '@shared/player/integration/playbackArea'

export interface IPlaybackProps {
  title?: string
  isBonus?: boolean
  sequenceNumber?: number
  playbackActions?: WpsRuntime.atv.wps.PlaybackActionsModelProps
  notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]
  entityType?: string
  purchaseDetails: PurchaseOffer | null
  isRefunding: boolean
  isDeeplinkableActionReady: boolean
}

export type EpTitleProps = IPlaybackProps & LocalizerProps & MaybeSpaContextProps

const NotificationActionsMessage = ({
  notificationActions,
}: {
  notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]
}) => (
  <>
    {(
      notificationActions ||
      ([] as WpsRuntime.atv.wps.NotificationActionProps[])
    )
      .filter(
        ({ severity }) => !severity || ['CRITICAL', 'ERROR'].includes(severity)
      )
      .map(notification => (
        <span className={st.playbackNotifications}>
          <Text string={notification.message} />
        </span>
      ))}
  </>
)

const PlaybackActionMessages = ({
  messages,
}: {
  messages?: WpsRuntime.atv.wps.StringList
}) =>
  messages && messages.length > 0 ? (
    <span className={st.playbackNotifications}>
      {messages.map((message, idx) => (
        <PlaybackActionMessage key={`pb-action-message-${idx}`} message={message} />
      ))}
    </span>
  ) : null

const PlaybackActionMessage = ({ message }: { message: String }) => (
  <span className={st.playbackMessage}>
    <Text string={message} />
  </span>
)

const EpTitle = ({
  title,
  sequenceNumber,
  isBonus,
  entityType,
}: {
  title?: string
  sequenceNumber?: number
  isBonus: boolean
  entityType?: string
}) => {
  if (!title) {
    return null
  }

  if (isLiveEntityType(entityType) || (!sequenceNumber && !isBonus)) {
    return <DecodedText string={title}/>
  }

  if (isBonus) {
    return (
      <TextLocalizer
        stringID={StringId.DV_DP_EL_bonus_title_template}
        replace={{ episodeTitle: title }}
        shouldDecode={true}
        dir='auto'
      />
    )
  }

  return (
    <TextLocalizer
      stringID={StringId.DV_DP_EL_episode_title_template}
      replace={{
        sequenceNumber: sequenceNumber ? sequenceNumber.toString() : '',
        episodeTitle: title
      }}
      shouldDecode={true}
      dir='auto'
    />
  )
}

const EpTitleWithMessages = (
  {
    title,
    purchaseDetails,
    sequenceNumber,
    isBonus = false,
    entityType,
    playbackActions,
    notificationActions,
    inProgress
  }: EpTitleProps & {inProgress: boolean}
) => (
  <div className={`dv-episode-noplayback-title ${st.playboxUnavailable}`}>
    <div
      className={cn(
        {
          [st.inProgress]: inProgress,
          [st.purchasing]: Boolean(purchaseDetails)
        },
        st.title
      )}
    >
      <EpTitle
        title={title}
        sequenceNumber={sequenceNumber}
        isBonus={isBonus}
        entityType={entityType}
      />
    </div>
    <NotificationActionsMessage notificationActions={notificationActions} />
    <PlaybackActionMessages
      messages={getPlaybackSecondaryMessages(playbackActions)}
    />
  </div>
)

const EpTitleWithPlayback = ({
  title,
  playbackActions,
  sequenceNumber,
  isBonus = false,
  entityType,
  getString,
  hasDownloadCompleted,
  getDownload,
  strings
}: EpTitleProps) => {
  const playbackData = getFirstPlaybackData(playbackActions)

  if (!playbackData) {
    return null
  }

  const progress = getPlaybackProgress(playbackData.progress)

  // build user-friendly accessibility description
  const ariaText = title
    ? sequenceNumber
      ? getString(StringId.DV_DP_ARIA_watch_episode, {
          episodeNumber: sequenceNumber.toString(),
          episodeTitle: title,
        })
      : getString(StringId.DV_DP_ARIA_watch_title, { title: title })
    : undefined // fallback to use the not-that-user-friendly text description

  const playbackTitleId = playbackData.playbackID
  const downloadCompleted =
    hasDownloadCompleted && hasDownloadCompleted(playbackTitleId)

  const playbackConfig = {
    signInUrl: playbackData.signInURL,
    playbackUrl: playbackData.playbackURL,
    fallbackUrl: playbackData.fallbackURL,
    contentConfig: {
      asin: playbackTitleId,
      trailer: false,
      position: playbackData.resumeTime,
      ref: getPlaybackClickstreamRef(
        playbackData.contentType,
        playbackData.entitlementType
      ),
      videoType: playbackData.videoMaterialType,
      useFullView: true,
    },
  }

  const downloadStatus = downloadCompleted && getDownload(playbackTitleId)
  const licenseModal = React.createRef<Modal>()

  const triggerOfflinePlayback = (e: React.MouseEvent) => {

    if(downloadStatus.showModalBeforePlay && !navigator.onLine) {
      licenseModal.current.open()
    } else {
      triggerWindowsPlayback(playbackTitleId, downloadStatus.download.DownloadId);
    }
    e.preventDefault()
  }

  return (
    <>
    <div className={`dv-episode-playback-title ${st.playbox}`}>
      <PlayerContext.Consumer>
        {({ onPlay }) => {
          const triggerPlayback = (e: React.MouseEvent) => {
            onPlay(playbackConfig)
            e.preventDefault()
          }

          const handler = downloadCompleted
            ? triggerOfflinePlayback
            :  triggerPlayback

          return (
            <PlayBox
              progress={progress}
              ariaLabel={ariaText}
              href={playbackData.playbackURL}
              onClick={handler}>
              <div className={`js-episode-title-name ${st.playboxActionLabel}`}>
                <EpTitle
                  title={title}
                  sequenceNumber={sequenceNumber}
                  isBonus={isBonus}
                  entityType={entityType}
                />
              </div>
            </PlayBox>
          )
        }}
      </PlayerContext.Consumer>
    </div>
    {(downloadStatus && downloadStatus.showModalBeforePlay) && <LicenseModal strings={strings} downloadStatus={downloadStatus.download} modalRef={licenseModal} />}
    </>
  )
}

export const EpisodePlayback: React.ComponentType<IPlaybackProps> = withMaybeSpaContext(
  withLocalization((props: EpTitleProps) => {
      const {
        playbackActions,
        purchaseDetails,
        isRefunding,
        isDeeplinkableActionReady
      } = props
      const playbackData = getFirstPlaybackData(playbackActions)
      const hasPlayback = Boolean(playbackData)

      const getPurchasingText = () => {
        if (hasPlayback) {
          return ''
        }
        return (
          purchaseDetails &&
          purchaseDetails.details &&
          purchaseDetails.details.offer.purchaseData.text
        ) || ''
      }

      const inProgress = Boolean(purchaseDetails) || isRefunding

      return (
        <div className={`js-eplist-episode ${widgetNameClasses.playback}`}>
          {isDeeplinkableActionReady && playbackData ? (
            <EpTitleWithPlayback {...props} />
          ) : (
            <EpTitleWithMessages {...props} inProgress={inProgress} />
          )}
          {inProgress && (
            <TransactionOverlay
              className={st.progressWrapper}
              text={getPurchasingText()}
              useSmallSpinner
            />
          )}
        </div>
      )
  })
)